import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import OrderDetails, {
  TicketData,
} from '../../../../core/models/order.details';
import { OrderService } from '../../services/order.service';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { LoadingComponent } from '../../../../core/components/loading/loading.component';
import { OrderTicketsComponent } from '../order-tickets/order-tickets.component';
import { TicketStatus } from '../../../../core/models/ticket.status';
import CouponType from '../../../../core/enums/couponType.enum';
import { CardType } from 'src/app/core/enums/cardType.enum';
import { OrderStatus } from 'src/app/core/enums/orderStatus.enum';
import {
  ExplicitCouponsType,
  ExplicitCouponType,
} from 'src/app/features/events/pages/checkout/checkout.component';

type ParsedTicketData = {
  ticketTypeId: number;
  qty: number;
  place?: string;
  ticketTypeName: string;
  ticketsLabel: string[];
  price: number;
  blockId?: string;
  isSeat: boolean;
};

@Component({
  selector: 'app-order-detail',
  templateUrl: './order-detail.component.html',
  styleUrls: ['./order-detail.component.css'],
})
export class OrderDetailComponent implements OnDestroy, AfterViewInit {
  @ViewChild(LoadingComponent) loading!: LoadingComponent;
  @ViewChild(OrderTicketsComponent)
  orderTicketsCompoent!: OrderTicketsComponent;

  public order: any | null = null;
  private _orderSubscriptor: Subscription | null = null;

  invalidOrder = false;
  isLoading = false;
  loadingOrderDetail = true;

  get codeDiscounts() {
    return (
      this.order?.discounts.filter(
        (d: any) => d?.coupon?.type !== CouponType.CreditCard
      ) || []
    );
  }

  get cardDiscount() {
    return this.order?.discounts.find(
      (d: any) => d?.coupon?.type == CouponType.CreditCard
    );
  }

  private pageSize = 10;
  page = 1;
  totalPages = 1;

  pageTickets: TicketData[] = [];

  public allCouponsNames: string = '';
  public totalDiscount: number = 0;
  public parsedTicketsData: ParsedTicketData[] = [];
  public codeCoupons: ExplicitCouponsType<CouponType.Code> = [];
  public cardCoupon?: ExplicitCouponType<CouponType.CreditCard>;

  constructor(
    private orderService: OrderService,
    private route: ActivatedRoute
  ) {}

  private isMobile(): boolean {
    return window.innerWidth <= 768;
  }

  getPageHeight(): number {
    if (this.isMobile()) {
      return window.innerHeight;
    } else {
      return Math.max(
        document.documentElement.scrollHeight,
        window.innerHeight
      );
    }
  }

  get totalTicketsCount(): number {
    return this.parsedTicketsData.reduce(
      (total, ticketData) => total + ticketData.qty,
      0
    );
  }

  get orderCancelled() {
    return this.order?.status == OrderStatus.CANCELLED;
  }

  get cardDigits() {
    return this.order?.cardType === CardType.SUPERCARD
      ? ''
      : this.order?.creditCard?.slice(6);
  }

  get cardLogo() {
    let base = '/assets/img/type-';
    if (this.order?.cardType === CardType.SUPERCARD) {
      return 'https://tix-shared.s3.us-east-1.amazonaws.com/type-supercard-card.png';
    }
    return base + this.order?.cardType + '-card.png';
  }

  get startIndex() {
    return (this.page - 1) * this.pageSize;
  }

  get endIndex() {
    return this.startIndex + this.pageSize;
  }

  ngAfterViewInit() {
    const slug = this.route.snapshot.params['order_slug'];
    this._orderSubscriptor = this.orderService
      .getDetail(slug)
      .subscribe((resp) => {
        this.order = resp;
        this.parsedTicketsData = this.getParsedTicketsData(resp?.tickets);
        this.totalDiscount = 0;
        this.codeCoupons = [];
        const couponNames: string[] = [];
        resp?.discounts.forEach((discount) => {
          const coupon = discount.coupon;
          if (discount.discounted) {
            this.totalDiscount += discount.discounted;
          }
          const couponLabel = coupon.name || coupon.code;
          if (couponLabel) {
            couponNames.push(couponLabel);
          }
          switch (coupon.type) {
            case CouponType.Code:
              this.codeCoupons.push(
                coupon as ExplicitCouponType<CouponType.Code>
              );
              break;
            case CouponType.CreditCard:
              this.cardCoupon =
                coupon as ExplicitCouponType<CouponType.CreditCard>;
              break;
          }
        });
        this.allCouponsNames = couponNames.join(', ');

        if (this.order == null) {
          this.invalidOrder = true;
        } else {
          this.totalPages = Math.ceil(
            this.order.tickets.length / this.pageSize
          );
          this.changePage();
        }

        this.loadingOrderDetail = false;
      });
  }

  nextPage() {
    if (this.page >= this.totalPages) return;

    this.page += 1;
    this.changePage();
  }

  prevPage() {
    if (this.page <= 1) return;

    this.page -= 1;
    this.changePage();
  }

  changePage() {
    this.pageTickets = this.order
      ? this.order.tickets.slice(this.startIndex, this.endIndex)
      : [];

    document.getElementById('ticket-1')?.scrollIntoView({ behavior: 'smooth' });
  }

  ngOnDestroy() {
    this._orderSubscriptor?.unsubscribe();
  }

  handleLoad() {
    this.loading.toggle('Preparando PDF...');
  }

  downloadTickets = async (forPrint = false) => {
    this.isLoading = true;
    this.loading.toggle();

    await this.orderTicketsCompoent.onClickDownload(forPrint);

    this.isLoading = false;
    this.loading.toggle();
  };

  getComments(): string[] {
    const comments: string[] = [];
    const ticketTypes: any = {};

    const ticketsComments = this.order?.event.ticketsComments;
    if (ticketsComments) {
      comments.push(ticketsComments);
    }

    this.order?.tickets.forEach((ticket: any) => {
      if (!ticketTypes[ticket.ticketType.id]) {
        const comment = ticket.ticketType.comment;
        if (comment) comments.push(comment);
        ticketTypes[ticket.ticketType.id] = true;
      }
    });

    return comments;
  }

  private getParsedTicketsData(tickets?: TicketData[]) {
    if (!tickets) {
      return [];
    }

    const data: ParsedTicketData[] = [];

    tickets.forEach((ticket) => {
      const ticketTypeId = ticket.ticketType.id;
      let index = data.findIndex((t) => t.ticketTypeId === ticketTypeId);
      if (index < 0) {
        data.push({
          qty: 0,
          price: ticket.price ?? ticket.ticketType.price,
          place: ticket.ticketType.place,
          ticketTypeId: ticketTypeId,
          ticketTypeName: ticket.ticketType.name,
          isSeat: ticket.ticketType.isSeat,
          blockId: ticket.ticketType.block_id,
          ticketsLabel: [],
        });

        index = data.length - 1;
      }

      const isSeat = data[index].isSeat;
      const blockId = data[index].blockId;

      let label = ticket.label;
      if (blockId && isSeat) {
        if (blockId) {
          const labelPrefix = blockId.replace('_', ' ') + ' ';
          label = label.replace(/\w+_/, ``);
        }
      }

      data[index].qty++;
      data[index].ticketsLabel.push(label);
    });
    return data;
  }

  hasCoupons(): boolean {
    return this.totalDiscount > 0;
  }

  getCouponsCount(): number {
    return this.allCouponsNames.split(',').length;
  }

  getCardConfig(cardType: string | undefined) {
    const configs: { [key: string]: { color: string; label: string } } = {
      visa: { color: '#1434CB', label: 'VISA' },
      mastercard: { color: '#FF5F00', label: 'MC' },
      supercard: { color: '#808080', label: 'SC' },
    };

    return (
      configs[cardType?.toLowerCase() || ''] || { color: '#666666', label: '?' }
    );
  }

  extractCardSuffix(cardNumber: string): string {
    // return cardNumber ? `•••• ${cardNumber.slice(-4)}` : '';
    return cardNumber ? `${cardNumber.slice(-4)}` : '';
  }
}
